import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import {
  ShieldCheck,
  PhoneCall,
  FileText,
  Layers,
  ListChecks,
  Heart,
} from "lucide-react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import AnimatedVerificationList from "@/components/layout/AnimatedVerificationList";
import { useSEO } from "@/hooks/use-seo";
import { fadeInUp } from "@/utils/animations";

const features = [
  {
    title: "Insurance Verification",
    description:
      "Verify insurance eligibility coverage with direct payer connections, even for obscure or local plans.",
    icon: ShieldCheck,
  },
  {
    title: "Automated Fallbacks",
    description:
      "We handle failed portal checks by verifying against other data sources directly from payers.",
    icon: PhoneCall,
  },
  {
    title: "Eligibility Breakdown",
    description:
      "See copays, deductibles, active coverage dates, and plan details. Download PDFs or push to your EHR.",
    icon: FileText,
  },
  {
    title: "Seamless Integration",
    description:
      "Talos integrates with your EHR, PMS, or custom workflow via API, file drop, or portal sync.",
    icon: Layers,
  },
  {
    title: "Audit-Ready Logging",
    description:
      "Every verification is traceable with timestamps, source metadata, and downloadable proof for compliance.",
    icon: ListChecks,
  },
  {
    title: "White-Glove Support",
    description:
      "Get customized onboarding, fast response times, and personalized installation with our team.",
    icon: Heart,
  },
];

const Home = () => {
  const [loaded, setLoaded] = useState(false);

  // SEO configuration for homepage
  useSEO({
    title: 'Talos - Automate Insurance Eligibility Checks for Healthcare',
    description: 'Talos automates insurance eligibility verification for healthcare providers. Say goodbye to manual phone calls and portal logins.',
    keywords: 'insurance verification, healthcare automation, eligibility checks, EHR integration, RPM, CCM, Talos, AI, automate insurance verification',
    canonical: 'https://runtalos.com',
    ogTitle: 'Talos - Automate Insurance Eligibility Checks for Healthcare',
    ogDescription: 'Talos automates insurance eligibility verifications for healthcare and medical teams, including RPM, CCM, community clinics, and health tech.',
    ogType: 'website',
    ogUrl: 'https://runtalos.com',
    twitterCard: 'summary_large_image',
    twitterTitle: 'Talos - Automate Insurance Eligibility Checks for Healthcare',
    twitterDescription: 'Talos automates insurance eligibility verification for healthcare providers.',
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "Talos",
      "description": "Automate insurance eligibility verification for healthcare providers",
      "url": "https://runtalos.com",
      "logo": "https://runtalos.com/public/logo.png",
      "sameAs": [],
      "contactPoint": {
        "@type": "ContactPoint",
        "contactType": "customer service",
        "url": "https://runtalos.com/demo"
      }
    }
  });

  useEffect(() => {
    const timer = setTimeout(() => setLoaded(true), 10);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div
        className={`transition-all duration-700 ease-out transform ${
          loaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
        } bg-white text-zinc-900 flex flex-col overflow-y gap-10 lg:gap-16 p-6 sm:px-6 sm:py-16 lg:px-0`}
      >
        {/* Hero (delayed) */}
        <div className="max-w-5xl mx-auto flex flex-col-reverse lg:flex-row lg:justify-between items-center gap-12 lg:gap-16">
          <motion.div
            className="flex flex-col w-full lg:w-1/2 items-center lg:items-start"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
          >
            <motion.h1
              className="text-4xl sm:text-5xl font-semibold mb-6 flex flex-wrap gap-2"
              initial="hidden"
              animate="visible"
            >
              {"Insurance Verification on Autopilot"
                .split(" ")
                .map((word, i) => (
                  <motion.span key={i}>
                    {word}
                  </motion.span>
                ))}
            </motion.h1>

            <p className="text-base sm:text-lg mb-6">
              Focus on patient care, save time, and let us deal with the
              insurance companies. Instantly check eligibility across any payer.
            </p>
            <motion.div>
              <Link 
                to="/demo" 
                className="inline-flex items-center gap-2 bg-zinc-900 text-white px-5 py-2 rounded-lg text-lg font-medium hover:bg-zinc-800 transition-colors"
              >
                Book a Demo
              </Link>
            </motion.div>
          </motion.div>
          <motion.div
            className="w-full max-w-md h-[350px] overflow-hidden rounded-lg bg-zinc-50 mb-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, ease: "easeOut", delay: 0.5 }}
          >
            <AnimatedVerificationList />
          </motion.div>
        </div>

        <div className="w-full border-b border-zinc-300" />

        {/* Features */}
        <motion.div
          className="mx-auto max-w-5xl"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <h2 className="text-3xl sm:text-4xl font-semibold mb-4">
            What We Offer
          </h2>
          <p className="text-base sm:text-lg text-zinc-700 mb-12">
            Healthcare eligibility verification tools built for accuracy, speed,
            and clean integration with your workflows.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={index}
                  className="w-full"
                  custom={index}
                  variants={fadeInUp}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                >
                  <Card className="hover:shadow-lg duration-300 ease-out rounded-lg">
                    <CardContent className="px-6 py-8 transition-transform hover:scale-[1.01] duration-300">
                      <div>
                        <Icon className="h-8 w-8 text-[#eab308] mb-4" />
                      </div>
                      <h3 className="text-2xl font-semibold mb-2">
                        {feature.title}
                      </h3>
                      <p className="text-zinc-700 text-base">
                        {feature.description}
                      </p>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        <div className="w-full border-b border-zinc-300" />

        {/* Comparison */}
        <motion.div
          className="mx-auto max-w-5xl"
          variants={fadeInUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <h2 className="text-3xl sm:text-4xl font-semibold mb-4">
            Reduce Claim Denials 
          </h2>
          <div className="text-base sm:text-lg text-zinc-700">
            Talos prevents avoidable claims denials by verifying procedure-level eligibility and increasing chances of a successful claim. 
            By handling fallbacks and various payer connectivity modes, we help improve your organization's revenue and financial health.
          </div>
        </motion.div>

        <div className="w-full border-b border-zinc-300" />

        <motion.div
          className="mx-auto max-w-5xl"
          variants={fadeInUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <h2 className="text-3xl sm:text-4xl font-semibold mb-4">
            Improve Patient Intake
          </h2>
          <div className="text-base sm:text-lg text-zinc-700">
            Talos makes it easy for patients to quickly verify their benefits before seeing a provider. 
            With real-time eligibility and developer APIs, Talos enables your organization to prevent drop off and provide streamlined customer experiences.
          </div>
        </motion.div>

        <div className="w-full border-b border-zinc-300" />

        {/* Schedule */}
        <motion.div
          className="mx-auto max-w-5xl w-full"
          variants={fadeInUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <h2 className="text-3xl sm:text-4xl font-semibold mb-6">
            Schedule A Call
          </h2>
          <iframe
            src="https://zcal.co/i/emBkLIru?embed=1&embedType=iframe"
            loading="lazy"
            className="w-full min-h-[800px] border-none"
            id="zcal-invite"
            title="Schedule A Call"
            style={{ height: 'auto' }}
          />
          {/* <iframe 
            src="https://calendar.google.com/calendar/appointments/schedules/AcZssZ2pDoeUukJqwpEuQUqlGwY7bhvMojUImWswofLnxKtaAQPwskl89t9TyGZQQYG7F1SFkDW6EwUY?gv=true" 
            className="w-full h-full min-h-[1100px] border-none"
            id="gcal-invite"
            title="Schedule A Call"
            loading="lazy"
          /> */}
        </motion.div>
      </div>
  );
};

export default Home;
